/**
 * The HUD's layout (M237, the overhaul's brief, section 1.4): on a phone held upright a bottom sheet over the ocean, its
 * three states (collapsed, the ocean nearly whole; half, the objectives and quick actions; full, the detail), and three
 * destinations under it (Explore, Manage, World); on a wider screen, or a phone on its side, a side panel that folds
 * away. Its rules here, apart from the page, so they are tested without a browser; `hud.ts` draws them.
 */

/** The three places the player goes (the brief's section 1.3). */
export type Pane = 'explore' | 'manage' | 'world';
export const PANES: readonly Pane[] = ['explore', 'manage', 'world'];
export const PANE_NAMES: Readonly<Record<Pane, string>> = { explore: 'Explore', manage: 'Manage', world: 'World' };

/** The sheet's states, the ocean seen most first; on a side panel only `collapsed` (folded) and the rest (open). */
export type SheetState = 'collapsed' | 'half' | 'full';
export const SHEET_STATES: readonly SheetState[] = ['collapsed', 'half', 'full'];

/** How the HUD is laid out on a screen: a bottom sheet, or a side panel. */
export type Layout = 'sheet' | 'side';

/**
 * The layout for a window, px: a side panel from 900 px wide (a tablet on its side, a laptop), and on a phone held
 * sideways, whose short height leaves a bottom sheet no ocean (520 px tall or less, wider than tall); else the sheet
 * (JUDGED: M105's phone sizes, the widest phone upright 430 px, a small tablet upright 744 px).
 */
export function layoutFor(width: number, height: number): Layout {
  return width >= 900 || (width > height && height <= 520) ? 'side' : 'sheet';
}

/** What a press on a destination does: the pane shown and the sheet's state after it. */
export interface NavStep {
  pane: Pane;
  state: SheetState;
}

/**
 * A press on a destination: another one opens at least halfway on it; the one shown, pressed again, folds the sheet
 * away (the ocean whole) or, folded, opens it halfway again. On a phone's sheet Manage opens to its full height in one
 * press (M304, the field loop's F3, the third playtest's: "opening management should never depend on a precise swipe");
 * Explore and World keep their half, the reef seen above them.
 */
export function navPress(pane: Pane, state: SheetState, pressed: Pane, layout: Layout = 'side'): NavStep {
  const open: SheetState = layout === 'sheet' && pressed === 'manage' ? 'full' : 'half';
  if (pressed !== pane) return { pane: pressed, state: state === 'collapsed' ? open : layout === 'sheet' && pressed === 'manage' ? 'full' : state };
  return { pane, state: state === 'collapsed' ? open : 'collapsed' };
}

/**
 * Where a drag of the sheet's handle leaves it: the state whose height is nearest the height it was let go at, unless
 * it was flicked (faster than `FLICK_PX_PER_MS`), when it goes on one state the way it was moving. Heights px, the
 * states' in `SHEET_STATES`' order; `dy` up negative.
 */
const FLICK_PX_PER_MS = 0.5;
export function dragEnd(state: SheetState, heights: readonly [number, number, number], dy: number, ms: number): SheetState {
  const from = SHEET_STATES.indexOf(state);
  const height = (heights[from] as number) - dy;
  const speed = ms > 0 ? -dy / ms : 0;
  if (Math.abs(speed) >= FLICK_PX_PER_MS) {
    const next = Math.max(0, Math.min(SHEET_STATES.length - 1, from + Math.sign(speed)));
    return SHEET_STATES[next] as SheetState;
  }
  let best = 0;
  heights.forEach((h, i) => { if (Math.abs(h - height) < Math.abs((heights[best] as number) - height)) best = i; });
  return SHEET_STATES[best] as SheetState;
}

/** A height for the sheet while it is dragged: never above its full state's nor below its collapsed one's. */
export function dragHeight(state: SheetState, heights: readonly [number, number, number], dy: number): number {
  const h = (heights[SHEET_STATES.indexOf(state)] as number) - dy;
  return Math.max(heights[0], Math.min(heights[2], h));
}

/** The keyboard's way through the states (the handle a button): each press one state on, from full back to collapsed. */
export function handlePress(state: SheetState): SheetState {
  return SHEET_STATES[(SHEET_STATES.indexOf(state) + 1) % SHEET_STATES.length] as SheetState;
}
